import { SEARCH_AND_FILTERS_CONDITION_PREFIX } from '@blaze-runtime/engine-domain';
import { type ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { getAllComparisonNodes } from '@blaze-shared/datasets-domain';
import { type BaseSchema } from '@blaze-shared/schema-domain';
import { type WorkflowNodeWithQuery } from '@blaze-shared/workflow-domain';

import { getComparisonNodeSourceId } from '../errors';

import {
  getNamedInputReferencesFromString,
  getParamReferencesFromString,
} from './get-references-from-string';
import { extractColumnsWithInputsFromString, extractTagsFromString } from './parse-string';
import {
  type WorkflowNodeColumnReference,
  type WorkflowNodeNamedInputReference,
  type WorkflowNodeParamReference,
  type WorkflowNodeTagReference,
} from './references.models';
import { findSchemaIdForColumn, findSchemaIdForInput, findSchemaIdForTag } from './schema.helpers';

export function getColumnReferencesFromQueryNode(
  node: WorkflowNodeWithQuery,
  schemas: Map<string | symbol, BaseSchema>,
): WorkflowNodeColumnReference[] {
  if (!node.query) {
    return [];
  }

  const references: WorkflowNodeColumnReference[] = [];
  getAllComparisonNodes(node.query)
    .filter((query) => !query.conditionId?.startsWith(SEARCH_AND_FILTERS_CONDITION_PREFIX))
    .forEach((queryNode) => {
      references.push(
        ...extractColumnsWithInputsFromString(
          queryNode.value ?? '',
          getComparisonNodeSourceId(queryNode, 'value'),
        ),
      );
      references.push(
        ...extractColumnsWithInputsFromString(
          queryNode.column,
          getComparisonNodeSourceId(queryNode, 'column'),
        ),
      );
    });

  return references.map((ref) => {
    if (ref.inputName) {
      ref.schemaId = findSchemaIdForInput(ref.inputName, ref.columns, schemas);
    } else {
      ref.schemaId = findSchemaIdForColumn(ref.columns[0], schemas);
    }

    return ref;
  });
}

export function getTagReferencesFromQueryNode(
  node: WorkflowNodeWithQuery,
  schemas: Map<string | symbol, BaseSchema>,
): WorkflowNodeTagReference[] {
  if (!node.query) {
    return [];
  }
  const references: WorkflowNodeTagReference[] = [];
  const tags = getAllComparisonNodes(node.query)
    .map((queryNode) => extractTagsFromString(queryNode.value ?? ''))
    .flat();

  for (const tag of tags) {
    references.push({
      tag,
      schemaId: findSchemaIdForTag(tag, schemas),
    });
  }

  return references;
}

export function getParamReferencesFromQueryNode(
  node: WorkflowNodeWithQuery,
  schemas: Map<string | symbol, BaseSchema>,
  connectors: ConnectorInfo[],
  sourceId?: string,
): WorkflowNodeParamReference[] {
  if (!node.query) {
    return [];
  }

  return getAllComparisonNodes(node.query).flatMap((queryNode) =>
    getParamReferencesFromString(queryNode.value, schemas, connectors, sourceId),
  );
}

export function getNamedInputReferencesFromQueryNode(
  node: WorkflowNodeWithQuery,
  schemas: Map<string | symbol, BaseSchema>,
): WorkflowNodeNamedInputReference[] {
  if (!node.query) {
    return [];
  }

  const references: WorkflowNodeNamedInputReference[] = [];
  getAllComparisonNodes(node.query)
    .filter((query) => !query.conditionId?.startsWith(SEARCH_AND_FILTERS_CONDITION_PREFIX))
    .forEach((queryNode) => {
      references.push(
        ...getNamedInputReferencesFromString(
          queryNode.value,
          schemas,
          getComparisonNodeSourceId(queryNode, 'value'),
        ),
      );
      references.push(
        ...getNamedInputReferencesFromString(
          queryNode.column,
          schemas,
          getComparisonNodeSourceId(queryNode, 'column'),
        ),
      );
    });

  return references.map((ref) => {
    return {
      ...ref,
      schemaId: findSchemaIdForInput(ref.namedInput, [], schemas),
    };
  });
}
